import { __awaiter } from './_virtual/_tslib.js';
import { getContractAddress } from './contracts/getContractAddress.js';
import ContractManager from './contracts/index.js';
import GqlManager from './GqlManager.js';
import singleCall from './utils/singleCall.js';
import writeTx from './utils/writeTx.js';
import * as index from './generated/factories/index.js';
export { index as factories };
export { Multicall__factory } from './generated/factories/Multicall__factory.js';
export { PublicResolverLogic__factory } from './generated/factories/PublicResolverLogic__factory.js';
export { PublicResolverProxy__factory } from './generated/factories/PublicResolverProxy__factory.js';
export { RNSDomainPriceLogic__factory } from './generated/factories/RNSDomainPriceLogic__factory.js';
export { RNSDomainPriceProxy__factory } from './generated/factories/RNSDomainPriceProxy__factory.js';
export { RNSUnifiedLogic__factory } from './generated/factories/RNSUnifiedLogic__factory.js';
export { RNSUnifiedProxy__factory } from './generated/factories/RNSUnifiedProxy__factory.js';
export { RONRegistrarControllerLogic__factory } from './generated/factories/RONRegistrarControllerLogic__factory.js';
export { RONRegistrarControllerProxy__factory } from './generated/factories/RONRegistrarControllerProxy__factory.js';
export { ReverseRegistrarLogic__factory } from './generated/factories/ReverseRegistrarLogic__factory.js';
export { ReverseRegistrarProxy__factory } from './generated/factories/ReverseRegistrarProxy__factory.js';
export { decodeContenthash, encodeContenthash, getProtocolType, isValidContenthash, validateContent } from './utils/contentHash.js';
export { ENSJSError, debugSubgraphError, debugSubgraphLatency, getClientErrors } from './utils/errors.js';
export { bracketFormat, formatHashed, truncateFormat } from './utils/format.js';
export { checkIsDecrypted, checkLabel, checkLocalStorageSize, decodeLabelhash, decryptName, encodeLabel, encodeLabelhash, getEncryptedLabelAmount, isEncodedLabelhash, keccakFromString, labelhash, parseName, saveLabel, saveName, truncateUndecryptedName } from './utils/labels.js';
export { beautify, emoji, isCombiningMark, namehash, normalise, normalizeFragment, shouldEscape, split, tokenise } from './utils/normalise.js';
export { generateABIInput, generateRecordCallArray, generateSetAddr, generateSingleRecordCall } from './utils/recordHelpers.js';
export { makeCommitment, makeCommitmentData, makeRegistrationData, randomSecret } from './utils/registerHelpers.js';
export { checkIsDotEth, parseInput, validateName } from './utils/validation.js';

/* eslint-disable @typescript-eslint/naming-convention */
const graphURIEndpoints = {
  2021: "https://saigon-thegraph.roninchain.com/subgraphs/name/axieinfinity/rns",
  2020: "https://thegraph-v2.roninchain.com/subgraphs/name/axieinfinity/rns"
};
class RNS {
  constructor(options) {
    this.getContractAddress = getContractAddress;
    this.gqlInstance = new GqlManager();
    /**
     * Checks for an initial provider and if it exists, sets it as the provider
     * @returns {Promise<void>} - A promise that resolves when the provider is checked, and set if needed
     */
    this.checkInitialProvider = () => __awaiter(this, void 0, void 0, function* () {
      if (!this.initialProvider) {
        return;
      }
      yield this.setProvider(this.initialProvider);
    });
    /**
     * Creates an object of ENS properties from an array
     * @param {FunctionDeps} dependencies - An array of ENS properties
     * @returns {Object} - An object of ENS properties
     */
    this.forwardDependenciesFromArray = dependencies =>
    // Creates an object from entries of the array
    Object.fromEntries(
    // Maps over dependencies and create arrays for each, e.g. ['contracts', contractObj]
    dependencies.map(dep => [dep, this[dep]]));
    // eslint-disable-next-line class-methods-use-this
    this.getModule = (path, exportName) => __awaiter(this, void 0, void 0, function* () {
      var _a;
      let mod = yield import( /* webpackMode: "lazy", webpackChunkName: "[request]", webpackPreload: true, webpackExclude: /.*\.ts$/ */
      `./functions/${path}`);
      // if export is nested in default, use default
      if ((_a = mod.default) === null || _a === void 0 ? void 0 : _a[exportName]) {
        mod = mod.default;
      }
      return mod;
    });
    // eslint-disable-next-line class-methods-use-this
    this.getFunction = (subFunc, writeable, exportName, mod,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _path) => subFunc && !writeable ? mod[exportName][subFunc] : mod[exportName];
    /**
     * Creates a wrapper for a function to be dynamically imported, with the correct dependencies passed in
     * @param {string} path - The path of the exported function
     * @param {FunctionDeps} dependencies - An array of ENS properties
     * @param {string} exportName - The export name of the target function
     * @param {string} subFunc - The type of function being imported
     * @returns {Function} - The generated wrapped function
     */
    this.importGenerator = (path, dependencies, exportName = "default", subFunc, passthrough) => {
      // if batch is specified, create batch func
      if (subFunc === "batch") {
        return (...args) => Object.assign({
          args
        }, passthrough);
      }
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const thisRef = this;
      const mainFunc = function (...args) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
          // check the initial provider and set if it exists
          yield thisRef.checkInitialProvider();
          // import the module dynamically
          const mod = yield thisRef.getModule(path, exportName);
          // if combine isn't specified, run normally
          // otherwise, create a function from the raw and decode functions
          if (subFunc !== "combine") {
            const writeable = subFunc === "write" || subFunc === "populateTransaction";
            // get the function to call
            const func = thisRef.getFunction(subFunc, writeable, exportName, mod, path);
            // get the dependencies to forward to the function as the first arg
            let dependenciesToForward = thisRef.forwardDependenciesFromArray(dependencies);
            // if func is write func, inject signer into dependencies
            if (writeable) {
              const options = args[1] || {};
              const signer = options.signer || ((_a = thisRef.provider) === null || _a === void 0 ? void 0 : _a.getSigner(options.addressOrIndex));
              const populate = subFunc === "populateTransaction";
              if (!signer) {
                throw new Error("No signer specified");
              }
              delete options.addressOrIndex;
              delete options.signer;
              dependenciesToForward = Object.assign(Object.assign({}, dependenciesToForward), {
                signer
              });
              return func(dependenciesToForward, args[0], options).then(writeTx(signer, populate));
            }
            // return the function with the dependencies forwarded
            return func(dependenciesToForward, ...args);
          }
          // get the dependencies to forward from raw and decode functions
          const dependenciesToForward = thisRef.forwardDependenciesFromArray(dependencies);
          // return singleCall function with dependencies forwarded
          return singleCall(thisRef.provider, dependenciesToForward, thisRef.getFunction(undefined, undefined, exportName, mod, path), ...args);
        });
      };
      // if subfunc is combine, add raw and decode property methods to the function
      if (subFunc === "combine") {
        mainFunc.raw = this.importGenerator(path, dependencies, exportName, "raw");
        mainFunc.decode = this.importGenerator(path, dependencies, exportName, "decode");
        mainFunc.batch = this.importGenerator(path, dependencies, exportName, "batch", {
          raw: mainFunc.raw,
          decode: mainFunc.decode
        });
      } else if (subFunc === "write") {
        mainFunc.populateTransaction = this.importGenerator(path, dependencies, exportName, "populateTransaction");
      }
      return mainFunc;
    };
    /**
     * Generates a normal wrapped function
     * @param {string} path - The path of the exported function
     * @param {FunctionDeps} dependencies - An array of ENS properties
     * @param {string} exportName - The export name of the target function
     * @returns {OmitFirstArg} - The generated wrapped function
     */
    this.generateFunction = (path, dependencies, exportName = "default") => this.importGenerator(path, dependencies, exportName);
    /**
     * Generates a write wrapped function
     * @param {string} path - The path of the exported function
     * @param {FunctionDeps} dependencies - An array of ENS properties
     * @param {string} exportName - The export name of the target function
     * @returns {OmitFirstArg} - The generated wrapped function
     */
    this.generateWriteFunction = (path, dependencies, exportName = "default") => this.importGenerator(path, dependencies, exportName, "write");
    /**
     * Generates a wrapped function from raw and decode exports
     * @param {string} path - The path of the exported function
     * @param {FunctionDeps} dependencies - An array of ENS properties
     * @param {string} exportName - The export name of the target function
     * @returns {GeneratedRawFunction} - The generated wrapped function
     */
    this.generateRawFunction = (path, dependencies, exportName = "default") => this.importGenerator(path, dependencies, exportName, "combine");
    /**
     * Sets the provider for the ENS class
     * @param {JsonRpcProvider} provider - The provider to set
     * @returns {Promise<void>} - A promise that resolves when the provider is set
     */
    this.setProvider = (provider, networkFixed) => __awaiter(this, void 0, void 0, function* () {
      var _b, _c;
      this.provider = provider;
      const network = networkFixed || this.staticNetwork ? (_c = (_b = this.provider) === null || _b === void 0 ? void 0 : _b._network) === null || _c === void 0 ? void 0 : _c.chainId : (yield this.provider.getNetwork()).chainId;
      if (this.options && this.options.graphURI) {
        this.graphURI = this.options.graphURI;
      } else {
        this.graphURI = graphURIEndpoints[network];
      }
      yield this.gqlInstance.setUrl(this.graphURI);
      this.contracts = new ContractManager(this.provider, this.getContractAddress(String(networkFixed)));
    });
    /**
     * Creates a new ENS instance with a different provider, ideally should be used individually with any given function
     * @param {JsonRpcProvider} provider - The provider to use
     * @returns {RNS} - A new ENS instance with the given provider
     */
    this.withProvider = provider => {
      const newRNS = new RNS(this.options);
      newRNS.initialProvider = provider;
      return newRNS;
    };
    this.batch = this.generateRawFunction("initialGetters", ["multicallWrapper"], "batch");
    this.getProfile = this.generateFunction("initialGetters", ["contracts", "gqlInstance", "getName", "resolverMulticallWrapper", "multicallWrapper", "_getAddr", "_getContentHash", "_getText"], "getProfile");
    this.getName = this.generateRawFunction("initialGetters", ["contracts"], "getName");
    this.getContentHash = this.generateRawFunction("initialGetters", ["contracts", "universalWrapper"], "getContentHash");
    this._getContentHash = this.generateRawFunction("initialGetters", ["contracts"], "_getContentHash");
    this.getAddr = this.generateRawFunction("initialGetters", ["contracts"], "getAddr");
    this.getText = this.generateRawFunction("initialGetters", ["contracts", "universalWrapper"], "getText");
    this._getText = this.generateRawFunction("initialGetters", ["contracts"], "_getText");
    this.getABI = this.generateRawFunction("initialGetters", ["contracts", "universalWrapper"], "getABI");
    this._getABI = this.generateRawFunction("initialGetters", ["contracts"], "_getABI");
    this.getNames = this.generateFunction("initialGetters", ["gqlInstance"], "getNames");
    this.getPrice = this.generateRawFunction("getPrice", ["contracts", "multicallWrapper"]);
    this.getAvailable = this.generateRawFunction("getAvailable", ["contracts"]);
    this.resolverMulticallWrapper = this.generateRawFunction("initialGetters", ["contracts"], "resolverMulticallWrapper");
    this.multicallWrapper = this.generateRawFunction("initialGetters", ["contracts", "provider"], "multicallWrapper");
    this.setName = this.generateWriteFunction("setName", ["contracts"]);
    this.setRecords = this.generateWriteFunction("setRecords", ["contracts", "provider"]);
    this.setRecord = this.generateWriteFunction("setRecord", ["contracts", "provider"]);
    this.commitName = this.generateWriteFunction("commitName", ["contracts"]);
    this.registerName = this.generateWriteFunction("registerName", ["contracts"]);
    this.renewNames = this.generateWriteFunction("renewNames", ["contracts"]);
    this.getDomainPrice = this.generateRawFunction("getDomainPrice", ["contracts"]);
    this.checkValid = this.generateRawFunction("checkValid", ["contracts"]);
    this.getRecord = this.generateRawFunction("getRecord", ["contracts"]);
    this.getGracePeriod = this.generateRawFunction("getGracePeriod", ["contracts"]);
    this.getRenewalFee = this.generateRawFunction("getRenewalFee", ["contracts"]);
    this.options = options;
    this.getContractAddress = (options === null || options === void 0 ? void 0 : options.getContractAddress) || getContractAddress;
  }
}

export { RNS, graphURIEndpoints };
